import React, { useState } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from './ui/button';
import { LayoutDashboard, Users, Settings, LogOut, Menu, X, User, Home, Clock, Star, Handshake, Shield, FileText, ScanLine, Briefcase, Key, CreditCard, AlertTriangle } from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const AdminLayout = () => {
  const { admin, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const menuItems = [
    { icon: LayoutDashboard, label: 'Dashboard', path: '/admin/dashboard' },
    { icon: Users, label: 'Abonnés', path: '/admin/customers' },
    { icon: Clock, label: 'En attente', path: '/admin/pending-subscriptions' },
    { icon: FileText, label: 'Paiements par facture', path: '/admin/invoice-payments' },
    { icon: Briefcase, label: 'Vendeurs', path: '/admin/sellers' },
    { icon: Key, label: 'Clés API', path: '/admin/api-keys', principalOnly: true },
    { icon: CreditCard, label: 'Config Stripe', path: '/admin/stripe-config', principalOnly: true },
    { icon: AlertTriangle, label: 'Système', path: '/admin/system', principalOnly: true },
    { icon: ScanLine, label: 'Logs OCR', path: '/admin/ocr-logs' },
    { icon: Star, label: 'Avis', path: '/admin/reviews' },
    { icon: Handshake, label: 'Partenaires', path: '/admin/partners' },
    { icon: Shield, label: 'Admins', path: '/admin/admin-users', principalOnly: true },
  ];

  const handleLogout = () => {
    logout();
    toast({
      title: 'Déconnexion',
      description: 'À bientôt !',
    });
    navigate('/admin/login');
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside
        className={`fixed top-0 left-0 z-50 h-full w-64 bg-white border-r border-gray-200 transform transition-transform duration-300 ease-in-out lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
      >
        <div className="h-full flex flex-col">
          {/* Logo */}
          <div className="p-6 border-b border-gray-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-gradient-to-br from-green-500 to-green-600 rounded-lg flex items-center justify-center">
                  <span className="text-white font-bold text-lg">🚗</span>
                </div>
                <div>
                  <h1 className="font-bold text-lg text-gray-900">Admin</h1>
                  <p className="text-xs text-gray-500">CarMaintenance</p>
                </div>
              </div>
              <button
                onClick={() => setSidebarOpen(false)}
                className="lg:hidden text-gray-500 hover:text-gray-700"
              >
                <X size={20} />
              </button>
            </div>
          </div>

          {/* Navigation */}
          <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
            {menuItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              const isDisabled = item.badge;
              const isPrincipalOnly = item.principalOnly && admin?.role !== 'principal';

              // Hide principal-only items for non-principal admins
              if (isPrincipalOnly) return null;

              return (
                <Link
                  key={item.path}
                  to={isDisabled ? '#' : item.path}
                  onClick={(e) => {
                    if (isDisabled) e.preventDefault();
                    setSidebarOpen(false);
                  }}
                  className={`flex items-center justify-between gap-3 px-4 py-3 rounded-lg transition-colors ${
                    isActive
                      ? 'bg-green-50 text-green-700 font-medium'
                      : isDisabled
                      ? 'text-gray-400 cursor-not-allowed'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <Icon size={20} />
                    <span>{item.label}</span>
                  </div>
                  {item.badge && (
                    <span className="text-xs bg-gray-200 text-gray-600 px-2 py-1 rounded-full">
                      {item.badge}
                    </span>
                  )}
                </Link>
              );
            })}
          </nav>

          {/* User Profile */}
          <div className="p-4 border-t border-gray-200">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-green-100 rounded-full flex items-center justify-center">
                <User size={20} className="text-green-600" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900 truncate">
                  {admin?.first_name} {admin?.last_name}
                </p>
                <p className="text-xs text-gray-500 truncate">{admin?.email}</p>
              </div>
            </div>
            <div className="space-y-1">
              <Link
                to="/admin/profile"
                onClick={() => setSidebarOpen(false)}
                className="block w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                Mon Profil
              </Link>
              <a
                href="/"
                className="flex items-center gap-2 w-full text-left px-3 py-2 text-sm text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
              >
                <Home size={16} />
                Retour au site
              </a>
              <button
                onClick={handleLogout}
                className="flex items-center gap-2 w-full text-left px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"
              >
                <LogOut size={16} />
                Déconnexion
              </button>
            </div>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="lg:ml-64">
        {/* Top Bar */}
        <header className="bg-white border-b border-gray-200 sticky top-0 z-30">
          <div className="px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
            <button
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden text-gray-500 hover:text-gray-700"
            >
              <Menu size={24} />
            </button>
            <div className="flex-1" />
            <div className="flex items-center gap-4">
              <a
                href="/"
                className="hidden md:flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900 transition-colors"
              >
                <Home size={16} />
                Retour au site
              </a>
              <span className="text-sm text-gray-600 hidden sm:inline">
                {admin?.first_name} {admin?.last_name}
              </span>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-4 sm:p-6 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default AdminLayout;
